import styled from '@emotion/styled';
import { breakpoints, LocalVideoTile, RemoteVideoTile, VideoControls, NavRow, useLayoutMode } from '../../ui';

// C1 / P1 / P7 layout: two large video tiles, the mic / camera / settings row under this user's
// own tile, and the action row at the bottom (End Session ... Add Notes, Begin).
//   portrait / phone: tiles stacked; landscape: side by side.

const Wrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
`;

const Tiles = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  gap: 16px 32px;
  align-content: start;
  ${({ mode }) =>
    mode === 'landscape'
      ? 'grid-template-columns: repeat(2, minmax(0, 1fr));'
      : 'grid-template-columns: minmax(0, 1fr);'}
`;

const TileBox = styled.div`
  width: 100%;
  aspect-ratio: var(--ratio);
`;

// The cell (tile + its controls) is as wide as the tile can be at its aspect ratio within the
// height left for it, so tile and controls line up.
const Cell = styled.div`
  --ratio: ${({ mode }) => (mode === 'landscape' ? 1.34 : mode === 'phone' ? 1.4 : 1.83)};
  --maxh: ${({ mode }) => (mode === 'landscape' ? 'calc(100dvh - 88px - 40px - 64px - 60px - 40px)' : 'calc((100dvh - 88px - 40px - 64px - 60px - 48px) / 2)')};
  width: min(100%, calc(var(--maxh) * var(--ratio)));
  justify-self: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  @media (max-width: ${breakpoints.phone - 1}px) {
    --maxh: calc((100dvh - 64px - 24px - 52px - 52px - 40px) / 2);
  }
`;

/**
 * @param localFirst  put this user's tile first (clinician lobby) or second (patient lobby)
 * @param people      from useSessionPeople()
 * @param left        node(s) at the left of the bottom row (End / Exit Session)
 * @param right       node(s) at the right of the bottom row (Add Notes, Begin)
 * @param note        optional node between tiles and actions (e.g. waiting message)
 */
export const LobbyView = ({ people, localFirst = true, left, right, note }) => {
  const mode = useLayoutMode();
  const local = (
    <Cell key='local' mode={mode}>
      <TileBox mode={mode}>
        <LocalVideoTile roleLabel={people.localLabel} name={people.localName} showMenu />
      </TileBox>
      <VideoControls settingsAlign='right' popPlacement={localFirst && mode !== 'landscape' ? 'bottom' : 'top'} />
    </Cell>
  );
  const remote = (
    <Cell key='remote' mode={mode}>
      <TileBox mode={mode}>
        <RemoteVideoTile roleLabel={people.remoteLabel} name={people.remoteName} />
      </TileBox>
    </Cell>
  );
  return (
    <Wrap data-testid='lobby' data-layout={mode}>
      <Tiles mode={mode}>{localFirst ? [local, remote] : [remote, local]}</Tiles>
      {note}
      <NavRow>
        {left}
        <div className='nav-right'>{right}</div>
      </NavRow>
    </Wrap>
  );
};

export default LobbyView;
